import Phaser from 'phaser';
import { responsiveScaleConfig, installOrientationGuard } from './ui/responsive';
import { BootScene } from './render/scenes/BootScene';
import { MenuScene } from './render/scenes/MenuScene';
import { LevelSelectScene } from './render/scenes/LevelSelectScene';
import { ShopScene } from './render/scenes/ShopScene';
import { EvolveScene } from './render/scenes/EvolveScene';
import { EquipScene } from './render/scenes/EquipScene';
import { RestaurantScene } from './render/scenes/RestaurantScene';
import { StorageScene } from './render/scenes/StorageScene';
import { AchievementsScene } from './render/scenes/AchievementsScene';
import { GameScene } from './render/scenes/GameScene';
import { HudScene } from './render/scenes/HudScene';
import { DemoScene } from './render/scenes/DemoScene';
import { HubScene } from './render/scenes/HubScene';
import { HubChapterScene } from './render/scenes/HubChapterScene';
import { OriginChapterScene } from './render/scenes/OriginChapterScene';
import { OriginShopScene } from './render/scenes/OriginShopScene';
import { CapybaraScene } from './render/scenes/CapybaraScene';
import { LeaderboardScene } from './render/scenes/LeaderboardScene';
import { AvatarShopScene } from './render/scenes/AvatarShopScene';
import { AvatarScene } from './render/scenes/AvatarScene';
import { TowerPickScene } from './render/scenes/TowerPickScene';
import { AdminInboxScene } from './render/scenes/AdminInboxScene';
import { EffectTimerScene } from './render/scenes/EffectTimerScene';
import { CreatorMenuScene } from './render/scenes/CreatorMenuScene';
import { ExploreScene } from './render/scenes/ExploreScene';
import { ExploreInfoScene } from './render/scenes/ExploreInfoScene';
import { ExploreChapterScene } from './render/scenes/ExploreChapterScene';
import { CreatorChapterScene } from './render/scenes/CreatorChapterScene';
import { CreatorSettingsScene } from './render/scenes/CreatorSettingsScene';
import { CreatorEditorScene } from './render/scenes/CreatorEditorScene';
import { CreatorWavesScene } from './render/scenes/CreatorWavesScene';
import { installRestockWatch } from './ui/restockWatch';
import { installEffectHeartbeat } from './ui/effectHeartbeat';
import { initAccountUi } from './account/accountUi';
import { initLevelNotices } from './ui/levelNoticePopup';
import { createAccountClient } from './account/supabaseClient';
import { initSaveSync } from './account/saveSync';
import { deliverForTest, initDevInbox } from './account/inbox';
import { initCodes } from './account/codes';
import { initPotionRestock } from './account/potionRestock';
import { initLeaderboard } from './account/leaderboardApi';
import { quests } from './render/quests';
import { saveStorage } from './render/saveStorage';

// ONE Supabase client for the whole app (account UI + save sync): a second
// createClient over the same storage key makes GoTrue warn about multiple
// instances. Null when unconfigured — everything downstream stays inert.
const accountClient = createAccountClient();

void (async () => {
  // Pull the account save BEFORE the game boots so scenes construct from it
  // (they read storage in their constructors). Errors are swallowed inside
  // initSaveSync — the game must always start.
  if (accountClient) await initSaveSync(accountClient);
  // Quests: take today's / this week's baseline before any play, so a session
  // that never opens the Hub still counts. Never fatal.
  try {
    quests().ensure(Date.now());
  } catch (err) {
    console.warn('[quests] ensure failed', err);
  }

  const game = new Phaser.Game({
    type: Phaser.AUTO,
    parent: 'game',
    backgroundColor: '#2e4a1f',
    scale: responsiveScaleConfig(),
    scene: [
      BootScene,
      MenuScene,
      LevelSelectScene,
      ShopScene,
      EvolveScene,
      EquipScene,
      RestaurantScene,
      StorageScene,
      AchievementsScene,
      GameScene,
      HudScene,
      DemoScene,
      HubScene,
      HubChapterScene,
      OriginChapterScene,
      OriginShopScene,
      CapybaraScene,
      LeaderboardScene,
      AvatarScene,
      AvatarShopScene,
      TowerPickScene,
      AdminInboxScene,
      EffectTimerScene,
      CreatorMenuScene,
      ExploreScene,
      ExploreInfoScene,
      ExploreChapterScene,
      CreatorChapterScene,
      CreatorSettingsScene,
      CreatorEditorScene,
      CreatorWavesScene,
    ],
  });

  installOrientationGuard();

  // Optional account system: renders nothing unless VITE_SUPABASE_* are baked in.
  initAccountUi(accountClient);

  // Leaderboards read through the SAME client: no client means no fourth tab.
  initLeaderboard(accountClient);

  // Redeem codes (spec 2026-10-09): no client means the CODES panel asks to sign in.
  initCodes(accountClient);

  // Dev-command deliveries (inbox drain + realtime). After the game exists so
  // a drained give lands in storage the already-booted scenes re-read on entry.
  if (accountClient) void initDevInbox(accountClient);
  if (accountClient) void initPotionRestock(accountClient);
  // A disapproved Explore level tells its maker why, once (spec 2026-11-02).
  if (accountClient) initLevelNotices(accountClient);

  // E2E-only: expose the Phaser game so tests can wait for a scene to be active
  // before clicking into it (avoids racing scene transitions), and the decorated
  // save storage so the auth e2e can exercise the write→debounce→upsert path
  // honestly. Stripped from normal production builds — only under VITE_E2E.
  if (import.meta.env.VITE_E2E) {
    (window as unknown as { __game?: Phaser.Game }).__game = game;
    (window as unknown as { __saveStorage?: unknown }).__saveStorage = saveStorage();
    (window as unknown as { __devDeliver?: unknown }).__devDeliver = deliverForTest;
  }

  // The Potion Shop restock banner (DOM, rides over every scene). The effect
  // timers are EffectTimerScene, launched by BootScene inside the canvas.
  installRestockWatch();
  // The Dark Shard's clock stops while the game is closed: the open game
  // stamps it once a minute (see ui/effectHeartbeat).
  installEffectHeartbeat();
})();
